import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HeaderContainer,
  AvatarProfile,
  AvatarDescription,
  Name,
  LastConnection,
  ContactDetailsWrapper,
  PersonalDetails,
  PersonalDetailsItem,
  CircleButton,
  TabSwitcherContainer,
  TabSubContainer,
  HeaderMasterContainer,
  TabRowContainer,
} from "./Header.styled";
import { Avatar, TabSwitcher, Skeleton, IconWithTooltip } from "@ann/web-components";
import HeaderEmailModal from "../HeaderEmailModal/HeaderEmailModal";
import HeaderPhoneModal from "../HeaderPhoneModal/HeaderPhoneModal";
import RemoveConfirmModal from "./RemoveConfirmModal";
import { useContactHeader } from "../../hooks/useContactHeader";
import { useContacts } from "../../context/ContactsContext";
import { useToast } from "../../../../../components/toast";

const Header = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { contactDetails, loading, hasContact } = useContactHeader();
  const { deleteEmail, deletePhone, selectedContact } = useContacts();
  const contactId = selectedContact?.partyUuid;

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isRemoveEmailModalOpen, setIsRemoveEmailModalOpen] = useState(false);
  const [isRemovePhoneModalOpen, setIsRemovePhoneModalOpen] = useState(false);
  const [pendingRemoveEmail, setPendingRemoveEmail] = useState(null);
  const [pendingRemovePhone, setPendingRemovePhone] = useState(null);

  const tabToRouteMap = {
    overview: "overview",
    activity: "activity",
    snapshot: "snapshot",
    garage: "garage",
    appointments: "appointments",
    "repair-orders": "repairs-orders",
    tasks: "tasks",
  };

  const getCurrentTabFromRoute = () => {
    const pathSegments = location.pathname.split("/");
    const ucpIndex = pathSegments.findIndex((segment) => segment === "ucp");
    if (ucpIndex === -1) return "overview";
    if (pathSegments.length <= ucpIndex + 1) return "overview";
    for (let i = ucpIndex + 1; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      for (const [tab, route] of Object.entries(tabToRouteMap)) {
        if (route === segment) return tab;
      }
    }
    return "overview";
  };

  const [selectedTab, setSelectedTab] = useState(getCurrentTabFromRoute());

  useEffect(() => {
    const currentTab = getCurrentTabFromRoute();
    setSelectedTab(currentTab);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    const route = tabToRouteMap[tabId];
    const pathSegments = location.pathname.split("/");
    const ucpIndex = pathSegments.findIndex((segment) => segment === "ucp");
    const contactIdFromPath = pathSegments[ucpIndex + 1];

    navigate(`/v2/ucp/${contactIdFromPath}/${route}`);
  };

  const handleOpenEmailModal = useCallback(() => setIsEmailModalOpen(true), []);
  const handleCloseEmailModal = useCallback(() => setIsEmailModalOpen(false), []);
  const handleOpenPhoneModal = useCallback(() => setIsPhoneModalOpen(true), []);
  const handleClosePhoneModal = useCallback(() => setIsPhoneModalOpen(false), []);

  const handleRequestRemoveEmail = useCallback((emailId, emailAddress) => {
    setIsEmailModalOpen(false);
    setPendingRemoveEmail({ id: emailId, email: emailAddress });
    setIsRemoveEmailModalOpen(true);
  }, []);

  const handleRequestRemovePhone = useCallback((phoneId, phoneNumber) => {
    setIsPhoneModalOpen(false);
    setPendingRemovePhone({ id: phoneId, phone: phoneNumber });
    setIsRemovePhoneModalOpen(true);
  }, []);

  const handleCloseRemoveEmailModal = useCallback(() => {
    setIsRemoveEmailModalOpen(false);
    setPendingRemoveEmail(null);
    setIsEmailModalOpen(true);
  }, []);

  const handleCloseRemovePhoneModal = useCallback(() => {
    setIsRemovePhoneModalOpen(false);
    setPendingRemovePhone(null);
    setIsPhoneModalOpen(true);
  }, []);

  const handleConfirmRemoveEmail = useCallback(async () => {
    if (!contactId || !pendingRemoveEmail) return;
    try {
      await deleteEmail(contactId, pendingRemoveEmail.id);
      toast.success({
        title: "Email removed",
        message: "The email was removed from this contact successfully.",
      });
    } catch (err) {
      console.error("Error removing email:", err);
      toast.error({
        title: "Error",
        message: err?.message || "Failed to remove email. Please try again.",
      });
      throw err;
    } finally {
      setIsRemoveEmailModalOpen(false);
      setPendingRemoveEmail(null);
    }
  }, [contactId, pendingRemoveEmail, deleteEmail, toast]);

  const handleConfirmRemovePhone = useCallback(async () => {
    if (!contactId || !pendingRemovePhone) return;
    try {
      await deletePhone(contactId, pendingRemovePhone.id);
      toast.success({
        title: "Phone number removed",
        message: "The phone number was removed from this contact successfully.",
      });
    } catch (err) {
      console.error("Error removing phone:", err);
      toast.error({
        title: "Error",
        message: err?.message || "Failed to remove phone. Please try again.",
      });
      throw err;
    } finally {
      setIsRemovePhoneModalOpen(false);
      setPendingRemovePhone(null);
    }
  }, [contactId, pendingRemovePhone, deletePhone, toast]);

  if (loading && !hasContact) {
    return (
      <HeaderMasterContainer>
        <HeaderContainer>
          <AvatarProfile>
            <Skeleton borderRadius="50%" height="60px" width="60px" />
          </AvatarProfile>
          <AvatarDescription>
            <Skeleton height="20px" width="200px" />
            <Skeleton height="16px" width="150px" />
          </AvatarDescription>
          <PersonalDetails>
            <div>
              <Skeleton height="16px" width="180px" />
            </div>
            <div>
              <Skeleton height="16px" width="200px" />
            </div>
          </PersonalDetails>
          <PersonalDetails>
            <div>
              <Skeleton height="16px" width="220px" />
            </div>
            <div>
              <Skeleton height="16px" width="100px" />
            </div>
          </PersonalDetails>
          <PersonalDetails>
            <div>
              <Skeleton height="16px" width="180px" />
            </div>
          </PersonalDetails>
          {/* Temporary disabled
          <ButtonsDetails>
            <Skeleton height="40px" width="40px" />
            <ButtonsContainer>
              <Skeleton height="40px" width="120px" />
            </ButtonsContainer>
          </ButtonsDetails> */}
        </HeaderContainer>
        <TabRowContainer>
          <TabSwitcherContainer>
            <TabSubContainer>
              <Skeleton height="40px" width="600px" />
            </TabSubContainer>
          </TabSwitcherContainer>
          {/* Temporary disabled
          <DepartmentSelectWrapper>
            <Skeleton height="40px" width="150px" />
          </DepartmentSelectWrapper> */}
        </TabRowContainer>
      </HeaderMasterContainer>
    );
  }

  if (!hasContact || !contactDetails) return null;

  return (
    <HeaderMasterContainer>
      <HeaderContainer>
        <AvatarProfile>
          <Avatar
            size="xl"
            src="https://i.pravatar.cc/60"
          />
        </AvatarProfile>
        <AvatarDescription>
          <Name>{contactDetails.name}</Name>
          <LastConnection>Last interaction: 3 days ago</LastConnection>
        </AvatarDescription>
        <ContactDetailsWrapper>
          <PersonalDetails>
            <div>
              <IconWithTooltip name="call" tooltip="Make a call" />
              <PersonalDetailsItem>{contactDetails.phone}</PersonalDetailsItem>
              <CircleButton onClick={handleOpenPhoneModal}>
                +{contactDetails.additionalPhonesCount || ""}
              </CircleButton>
            </div>
            <div>
              <IconWithTooltip name="email" tooltip="Send Mail" />
              <PersonalDetailsItem>{contactDetails.email}</PersonalDetailsItem>
              <CircleButton onClick={handleOpenEmailModal}>
                +{contactDetails.additionalEmailsCount || ""}
              </CircleButton>
            </div>
          </PersonalDetails>
          <PersonalDetails>
            <div>
              <IconWithTooltip name="locationOn" tooltip="Location" />
              <PersonalDetailsItem>
                {contactDetails.address || 'No address available'}
              </PersonalDetailsItem>
            </div>
            {contactDetails.postalCode !== 'N/A' && (
              <div>
                <IconWithTooltip name="tag" tooltip="Postal Code" />
                <PersonalDetailsItem>{contactDetails.postalCode}</PersonalDetailsItem>
              </div>
            )}
          </PersonalDetails>
        </ContactDetailsWrapper>
        {contactDetails.company !== 'N/A' && (
          <PersonalDetails>
            <div>
              <IconWithTooltip name="work" tooltip="Work" />
              <PersonalDetailsItem>{contactDetails.company}</PersonalDetailsItem>
            </div>
          </PersonalDetails>
        )}
        
        {/* Temporary disabled
        <ButtonsDetails>
          <ActionsButton>
            <Icon name="edit" color="#5C5C5C" />
          </ActionsButton>
          <ButtonsContainer>
            <Button type="secondary" size="medium" label="View Family" pill />
          </ButtonsContainer>
        </ButtonsDetails> */}
      </HeaderContainer>
      <TabRowContainer>
        <TabSwitcherContainer>
          <TabSubContainer>
            <TabSwitcher
              key={selectedTab}
              defaultSelectedId={selectedTab}
              size="medium"
              onChange={handleTabChange}
            >
              <TabSwitcher.Button id="overview" label="Overview" />
              <TabSwitcher.Button id="activity" label="Activity" />
              <TabSwitcher.Button id="snapshot" label="Snapshot" />
              <TabSwitcher.Button id="garage" label="Garage" />
              <TabSwitcher.Button id="appointments" label="Appointments" />
              <TabSwitcher.Button id="repair-orders" label="Repair Orders" />
              <TabSwitcher.Button id="tasks" label="Tasks" />
            </TabSwitcher>
          </TabSubContainer>
        </TabSwitcherContainer>
        {/* Temporary disabled
        <DepartmentSelectWrapper>
          <Select
            placeholder="Sales"
            onChange={(value) => console.log("Selected:", value)}
            size="small"
            value="sales"
            minWidth="100px"
          >
            <Select.Menu>
              <Select.Item value="sales" label="Sales">
                Sales
              </Select.Item>
              <Select.Item value="support" label="Support">
                Support
              </Select.Item>
              <Select.Item value="marketing" label="Marketing">
                Marketing
              </Select.Item>
              <Select.Item value="customize" label="Customize">
                Customize
              </Select.Item>
            </Select.Menu>
          </Select>
        </DepartmentSelectWrapper> */}
      </TabRowContainer>
      <HeaderEmailModal
        isOpen={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        onRequestRemove={handleRequestRemoveEmail}
      />
      <HeaderPhoneModal
        isOpen={isPhoneModalOpen}
        onClose={handleClosePhoneModal}
        onRequestRemove={handleRequestRemovePhone}
      />
      <RemoveConfirmModal
        isOpen={isRemoveEmailModalOpen}
        onClose={handleCloseRemoveEmailModal}
        title="Remove email?"
        description={`You're removing ${pendingRemoveEmail?.email ?? ""} from this contact.`}
        confirmLabel="Remove email"
        onConfirm={handleConfirmRemoveEmail}
      />
      <RemoveConfirmModal
        isOpen={isRemovePhoneModalOpen}
        onClose={handleCloseRemovePhoneModal}
        title="Remove phone number?"
        description="This phone number will be removed from the contact and won't be used for calls or messages anymore. You can add it back later if needed."
        confirmLabel="Remove number"
        onConfirm={handleConfirmRemovePhone}
      />
    </HeaderMasterContainer>
  );
});

export default Header;
